import { useMemo, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useShallow } from 'zustand/react/shallow';
// mantine
import { DataTable } from 'mantine-datatable';
import { Drawer, TextInput } from '@mantine/core';
import {
    useDebouncedState,
    useDebouncedValue,
    useDisclosure
} from '@mantine/hooks';
// icons
import { ListFilter } from 'lucide-react';
// components
import { ColumnsToggle, ResponsiveButton } from '@newo/ui-kit';
import { getFiltersCount } from '@newo/toolkit/bff';
import PageContainer from '@/components/Layout/components/PageContainer';
import Filters from '@/pages/Sessions/components/Filters';
import ExportSessionsButton from '@/pages/Sessions/components/ExportSessionsButton';
import URLListener from '@/pages/Sessions/components/URLListener';
// utils
import { cn } from '@/utils';
import { useSessionsQuery } from '@/services/bff/bff.queries.ts';
import {
    SESSIONS_TABLE_STORE_KEY,
    PAGE_SIZES
} from '@/pages/Sessions/Sessions.utils.ts';
import { getDateRange } from '@/utils/filters';
import { useColumns } from '@/pages/Sessions/Sessions.hooks';
import useSessionsStore from '@/pages/Sessions/Sessions.store.ts';
import Header from '@/components/Header';

const DEBOUNCE_TIMEOUT = 1000;
const DEFAULT_TOTAL = 0;

const Sessions = () => {
    const [isOpened, handlers] = useDisclosure();
    const [query, setQuery] = useDebouncedState('', DEBOUNCE_TIMEOUT);

    const {
        hiddenColumns,
        setHiddenColumns,
        filters,
        dateRange,
        pagination,
        setPagination
    } = useSessionsStore(
        useShallow((state) => ({
            hiddenColumns: state.hiddenColumns,
            setHiddenColumns: state.setHiddenColumns,
            filters: state.filters,
            dateRange: state.dateRange,
            pagination: state.pagination,
            setPagination: state.setPagination
        }))
    );

    const [debounced] = useDebouncedValue(filters, DEBOUNCE_TIMEOUT);

    const { effectiveColumns, resetColumns } = useColumns();

    const _dateRange = useMemo(() => {
        if (dateRange) {
            return getDateRange(dateRange);
        }
        return {};
    }, [dateRange]);

    const { data, isLoading } = useSessionsQuery({
        ...debounced,
        ..._dateRange,
        ...pagination,
        query: query || undefined,
        is_active: false
    });

    const sessions = useMemo(() => data?.items ?? [], [data]);
    const total = data?.metadata.total ?? DEFAULT_TOTAL;
    const counter = getFiltersCount({ ...filters, dateRange }, ['page', 'per']);

    useEffect(() => {
        setPagination({ page: 1 });
    }, [query]);

    const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        setQuery(event.currentTarget.value);
    };

    const handlePageChange = (newPage: number) => {
        setPagination({ page: newPage });
    };

    const handlePageSizeChange = (newPageSize: number) => {
        const totalPages = Math.ceil(total / newPageSize);
        const newPage = pagination.page > totalPages ? 1 : pagination.page;
        setPagination({ per: newPageSize, page: newPage });
    };

    const handleDrawerClose = () => {
        handlers.close();
    };

    const handleFiltersToggle = () => {
        handlers.toggle();
    };

    const columns = effectiveColumns.map(({ accessor, title }) => ({
        accessor,
        title
    }));

    const handleHiddenChange = (hidden: string[]) => {
        setHiddenColumns(hidden);
    };

    const handleReset = () => {
        resetColumns();
    };

    return (
        <>
            <Helmet>
                <title>NEWO - Sessions</title>
            </Helmet>
            <Header title="Sessions" />
            <PageContainer>
                <div className="flex justify-between items-end gap-2 mb-4">
                    <TextInput
                        className="flex-1 md:max-w-lg mr-auto"
                        label="Search"
                        placeholder="Search by phone number, name"
                        size="xs"
                        onChange={handleSearch}
                    />
                    <ExportSessionsButton filters={filters} total={total} />
                    <ColumnsToggle
                        columns={columns}
                        exclude={['datetime', 'actions']}
                        hidden={hiddenColumns}
                        onHiddenChange={handleHiddenChange}
                        onReset={handleReset}
                    />

                    <ResponsiveButton
                        counter={counter}
                        icon={<ListFilter />}
                        onClick={handleFiltersToggle}
                    >
                        Filters
                    </ResponsiveButton>
                </div>
                <DataTable
                    highlightOnHover
                    pinLastColumn
                    columns={effectiveColumns}
                    fetching={isLoading}
                    height="83vh"
                    idAccessor="id"
                    loaderBackgroundBlur={1}
                    loaderColor="dark"
                    page={pagination.page}
                    records={sessions}
                    recordsPerPage={pagination.per}
                    recordsPerPageOptions={PAGE_SIZES}
                    rowClassName={(session) => {
                        return cn({
                            'bg-[#ffefef] hover:bg-red-1':
                                session.result_success === false,
                            'bg-green-1 hover:bg-green-2':
                                session.result_success === true
                        });
                    }}
                    scrollAreaProps={{
                        scrollbarSize: 10,
                        type: 'always'
                    }}
                    storeColumnsKey={SESSIONS_TABLE_STORE_KEY}
                    totalRecords={total}
                    verticalAlign="top"
                    onPageChange={handlePageChange}
                    onRecordsPerPageChange={handlePageSizeChange}
                />
            </PageContainer>
            <Drawer
                opened={isOpened}
                position="right"
                title="Filters"
                onClose={handleDrawerClose}
            >
                <Filters total={total} />
            </Drawer>
            <URLListener />
        </>
    );
};

export default Sessions;
